import { inject, Injectable } from '@angular/core';
import { ChartDataService } from '@app/core/services/charts/chart-data.service';
import { ChartThemeService } from '@app/core/services/charts/chart-theme.service';
import { ChartOptions, TooltipOptions } from 'chart.js';
import { _, TranslateService } from '@ngx-translate/core';
import { DeviceService } from '@app/core/services/device/device.service';
import { LocaleNumberPipe } from '@app/shared/pipes/locale-number/locale-number.pipe';
import { ChartZoomHelpService } from '@app/core/services/charts/chart-zoom-help.service';
import { ZoomControlsAction } from '@app/core/services/charts/plugins/zoom-controls.plugin';
import { ColorManagementService } from '@app/core/services/colors/color-management.service';
import { _DeepPartialObject } from 'node_modules/chart.js/dist/types/utils';
import { IFloatingPopupChartTranslationKeys } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-chart-translation-keys';
import { FloatingPopupBaseChartService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-base-chart.service';

@Injectable({ providedIn: 'root' })
export class FloatingPopupLineChartService extends FloatingPopupBaseChartService {
  public translate: TranslateService = inject(TranslateService);
  public localeNumber = inject(LocaleNumberPipe);
  protected chartThemeService = inject(ChartThemeService);
  protected chartDataService = inject(ChartDataService);
  protected deviceService = inject(DeviceService);
  protected zoomHelpService = inject(ChartZoomHelpService);
  protected colorManagementService = inject(ColorManagementService);

  public generateOptions(
    chartTranslation: IFloatingPopupChartTranslationKeys,
    minXAxis: number | null,
    maxXAxis: number | null,
    beginAtZero: boolean,
    customTooltipConfig: _DeepPartialObject<TooltipOptions>,
    interactionsEnabled = true
  ): ChartOptions {
    const translatedYLabel = chartTranslation.yLabel ? this.chartElementTranslation(chartTranslation.yLabel) : null;
    const translatedXLabel = chartTranslation.xLabel ? this.chartElementTranslation(chartTranslation.xLabel) : null;
    const translatedTitleText = chartTranslation.titleText
      ? this.chartElementTranslation(chartTranslation.titleText)
      : null;
    const scrollModifierKey = this.deviceService.isMacOS() ? 'meta' : 'ctrl';

    return {
      ...this.chartThemeService.getBaseOptions(),
      layout: {
        padding: 0,
      },
      scales: {
        x: {
          ...this.chartThemeService.getXScaleOptions(),
          type: 'linear',
          bounds: 'data',
          stacked: true,
          beginAtZero: false,
          suggestedMin: minXAxis || undefined,
          suggestedMax: maxXAxis || undefined,
          ticks: {
            precision: 0,
            autoSkip: true,
            maxTicksLimit: 250,
            color: this.chartThemeService.getColors().standardLabelColor,
            backdropColor: this.chartThemeService.getColors().transparent,
            backdropPadding: { top: 2, bottom: 2, left: 4, right: 4 },
            showLabelBackdrop: false,
          },
          title: {
            ...this.chartThemeService.getScaleTitleOptions(),
            display: !!translatedXLabel,
            text: translatedXLabel,
          },
        },
        y: {
          ...this.chartThemeService.getYScaleOptions(),
          stacked: true,
          beginAtZero,
          title: {
            ...this.chartThemeService.getScaleTitleOptions(),
            display: !!translatedYLabel,
            text: translatedYLabel,
          },
          ticks: {
            ...this.chartThemeService.getYScaleOptions().ticks,
            callback: value => this.formatChartNumber(Number(value), 0, 2),
          },
        },
      },
      elements: {
        line: {
          tension: 0,
        },
      },
      plugins: {
        scrollPan: {
          display: interactionsEnabled,
          // delete this if we need show all data without scroll
          init: { minTickWidth: 0 },
        },
        hoverVerticalLine: {
          display: true,
          lineColor: this.chartThemeService.getColors().hoverLine,
          lineDash: [4, 2],
          lineWidth: 1,
        },
        zoom: {
          pan: {
            enabled: interactionsEnabled,
            mode: 'x',
          },
          limits: {
            x: {
              min: minXAxis || 'original',
              max: maxXAxis || 'original',
            },
          },
          zoom: {
            wheel: { enabled: interactionsEnabled, modifierKey: scrollModifierKey },
            pinch: { enabled: interactionsEnabled },
            mode: 'x',
          },
        },
        tooltip: {
          ...this.chartThemeService.getTooltipOptions(),
          mode: 'index',
          bodySpacing: 6,
          titleSpacing: 6,
          titleMarginBottom: 6,
          displayColors: false,
          itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
          ...customTooltipConfig,
        },
        legend: {
          display: false,
        },
        title: {
          display: !!translatedTitleText,
          text: translatedTitleText,
          color: this.chartThemeService.getColors().titleColor,
          padding: {
            top: 5,
            bottom: 0,
          },
          font: {
            ...this.chartThemeService.getBaseFont(),
          },
        },
        zoomControls: {
          enabled: interactionsEnabled,
          zoomStep: '10%',
          zoomInLabel: this.translate.instant(_('@@chart-zoom-controls__zoom-in')),
          zoomOutLabel: this.translate.instant(_('@@chart-zoom-controls__zoom-out')),
          resetZoomLabel: this.translate.instant(_('@@chart-zoom-controls__reset-zoom')),
          onZoomControlClick: (action, wrapper) => {
            if (action === ZoomControlsAction.Out || action === ZoomControlsAction.In) {
              this.zoomHelpService.handleZoomHelpDialog({
                marginTop: translatedTitleText ? '1.375rem' : '0.3125rem',
                ...(wrapper ? { appendTo: wrapper } : {}),
              });
            }
          },
        },
      },
      interaction: {
        mode: 'x',
        intersect: false,
      },
      hover: {
        mode: 'x',
        intersect: false,
      },
    };
  }
}
